import { Button, Dropdown, Field, Input, Option, Spinner, Text } from '@fluentui/react-components';
import type { ProvisionTenantResult, SubscriptionTier, TierKey } from '@worklogix/shared';
import { useCallback, useEffect, useState } from 'react';

import { usePlatformApi } from '../api';
import { Blade } from '../components/Blade';

import { toErrorMessage } from './errors';

/** Slug charset contract shared with the api / provision function. */
const SLUG_PATTERN = /^[a-z0-9-]+$/;

/** Default subscription tier for a newly provisioned tenant (soft-warn model). */
const DEFAULT_TIER: TierKey = 'free';

export interface CreateTenantBladeProps {
  /** Whether the create blade is open. */
  open: boolean;
  /** Close the blade (cancel or done). */
  onClose: () => void;
  /** Fired after any successful provision (new or idempotent) so the list reloads. */
  onCreated: () => void;
}

/**
 * Create-tenant blade: a Fluent form (name + slug) that provisions a tenant via
 * {@link provisionTenant}. Slug is validated against {@link SLUG_PATTERN}. A new
 * tenant closes the blade; an idempotent hit (`created === false`) keeps the
 * blade open with an informational "already existed" state. Both refresh the
 * list so the caller reflects current server state either way.
 */
export function CreateTenantBlade({
  open,
  onClose,
  onCreated,
}: CreateTenantBladeProps): JSX.Element {
  const api = usePlatformApi();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [tier, setTier] = useState<TierKey>(DEFAULT_TIER);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existed, setExisted] = useState<ProvisionTenantResult | null>(null);

  // Load the tier catalog for the picker while the blade is open. A failed load
  // is non-fatal: the tenant still provisions at the default ('free') tier.
  useEffect(() => {
    if (!open) {
      return;
    }
    let active = true;
    api
      .getTiers()
      .then((catalog) => {
        if (active) {
          setTiers(catalog);
        }
      })
      .catch(() => {
        if (active) {
          setTiers([]);
        }
      });
    return () => {
      active = false;
    };
  }, [api, open]);

  const reset = useCallback(() => {
    setName('');
    setSlug('');
    setTier(DEFAULT_TIER);
    setSubmitting(false);
    setError(null);
    setExisted(null);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const slugInvalid = slug.length > 0 && !SLUG_PATTERN.test(slug);
  const canSubmit = name.trim().length > 0 && slug.length > 0 && !slugInvalid && !submitting;

  // Only attach validation props when the slug is actually invalid — under
  // exactOptionalPropertyTypes we must not pass `validationMessage={undefined}`.
  const slugValidation = slugInvalid
    ? {
        validationState: 'error' as const,
        validationMessage: 'Use lowercase letters, numbers, and hyphens only.',
      }
    : {};

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.provisionTenant({
        name: name.trim(),
        slug,
        subscriptionTier: tier,
      });
      onCreated();
      if (result.created) {
        reset();
        onClose();
      } else {
        setExisted(result);
        setSubmitting(false);
      }
    } catch (err: unknown) {
      setError(toErrorMessage(err));
      setSubmitting(false);
    }
  }, [api, name, slug, tier, onCreated, onClose, reset]);

  const selectedTierName = tiers.find((t) => t.key === tier)?.name ?? tier;

  return (
    <Blade open={open} onClose={handleClose} title="Create tenant">
      {existed ? (
        <div className="wlx-blade-info" role="status">
          <Text>
            A tenant with the slug “{existed.tenant.slug}” already exists. No changes were made.
          </Text>
          <div className="wlx-form-actions">
            <Button appearance="primary" onClick={handleClose}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        <form
          className="wlx-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) {
              void handleSubmit();
            }
          }}
        >
          <Field label="Name" required>
            <Input
              value={name}
              onChange={(_, data) => setName(data.value)}
              disabled={submitting}
              placeholder="Acme Co."
            />
          </Field>

          <Field label="Slug" required {...slugValidation}>
            <Input
              value={slug}
              onChange={(_, data) => setSlug(data.value)}
              disabled={submitting}
              placeholder="acme-co"
            />
          </Field>

          <Field label="Subscription tier">
            <Dropdown
              value={selectedTierName}
              selectedOptions={[tier]}
              disabled={submitting || tiers.length === 0}
              onOptionSelect={(_, data) => {
                const picked = tiers.find((t) => t.key === data.optionValue);
                if (picked) {
                  setTier(picked.key);
                }
              }}
            >
              {tiers.map((t) => (
                <Option key={t.key} value={t.key} text={t.name}>
                  {t.name}
                </Option>
              ))}
            </Dropdown>
          </Field>

          {error ? (
            <Text role="alert" className="wlx-form-error">
              {error}
            </Text>
          ) : null}

          <div className="wlx-form-actions">
            <Button appearance="secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button appearance="primary" type="submit" disabled={!canSubmit}>
              {submitting ? <Spinner size="tiny" /> : 'Create'}
            </Button>
          </div>
        </form>
      )}
    </Blade>
  );
}
